import React, { useMemo } from "react";
import styled, { css } from "styled-components";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import Select from "common/Select/Select.tsx";
import TextWithIcon from "common/Text/TextWithIcon.tsx";
import { ICON_TYPES } from "@shared/common/constants.ts";
import { checkSearch } from "@shared/utils/misc.ts";
import Title from "common/Typography/Title.tsx";
import { HelperTooltip } from "common/Tooltips/HelperTooltip.tsx";
import RequiredFieldMessage from "common/Animations/RequiredFieldMessage.tsx";
import Checkbox from "common/Checkbox/Checkbox.tsx";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import { AnalyticsPageName, LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { phoneMaxWidth } from "common/NewLook/layout.ts";

interface ExchangesSelectorProps {
  allExchanges: string[];
  supportedExchanges: string[] | null;
  setSupportedExchanges: (exchanges: string[]) => void;
  supportedExchangesError: boolean;
  includeNewExchanges: boolean;
  setIncludeNewExchanges: (include: boolean) => void;
  allSelected: boolean;
  allSupportedExchanges: boolean;
  isBottomNavigator: boolean;
}

const ExchangesSelector = ({
  allExchanges,
  supportedExchanges,
  setSupportedExchanges,
  supportedExchangesError,
  includeNewExchanges,
  setIncludeNewExchanges,
  allSelected,
  allSupportedExchanges,
  isBottomNavigator,
}: ExchangesSelectorProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ASSET_MANAGER);

  const exchangesOptions = useMemo(
    () =>
      allExchanges.map((exchange) => ({
        label: (
          <TextWithIcon
            iconType={ICON_TYPES.EXCHANGE}
            text={exchange}
            coinOrExchangeName={exchange}
          />
        ),
        value: exchange,
      })),
    [allExchanges]
  );

  const includeNewlyExchangesCheckbox = (
    <StyledCheckbox
      checked={includeNewExchanges}
      onChange={(e: CheckboxChangeEvent) =>
        setIncludeNewExchanges(e.target.checked)
      }
      disabled={!allSelected}
    >
      Include new exchanges
      <HelperTooltip
        analyticsPageName={AnalyticsPageName.ASSET_MANAGER}
        analyticsFieldName={t(
          "configuration.tooltips.include_newly_exchanges_title"
        )}
        title={t("configuration.tooltips.include_newly_exchanges_title")}
        description={t(
          "configuration.tooltips.include_newly_exchanges_description"
        )}
        iconStyle={{ top: isBottomNavigator ? 3 : 0, position: "relative" }}
      />
    </StyledCheckbox>
  );

  return (
    <Item>
      <ItemTitle>
        {t("configuration.titles.supported_exchanges")}
        <HelperTooltip
          analyticsPageName={AnalyticsPageName.ASSET_MANAGER}
          analyticsFieldName={t("configuration.titles.supported_exchanges")}
          title={t("configuration.titles.supported_exchanges")}
          description={t(
            "configuration.tooltips.supported_exchanges_description"
          )}
        />
      </ItemTitle>
      <Select
        mode="multiple"
        options={exchangesOptions}
        value={supportedExchanges}
        onChange={setSupportedExchanges}
        placeholder={t("configuration.placeholders.supported_exchanges")}
        maxTagPlaceholder={
          allSupportedExchanges
            ? t("configuration.titles.all_supported_exchanges")
            : undefined
        }
        maxTagCount={allSupportedExchanges ? 0 : 8}
        style={{ width: "100%" }}
        showSearch={{
          filterOption: (searchValue, option) =>
            checkSearch(option?.value, searchValue),
        }}
        allowClear={true}
        mobileTitle={t("configuration.titles.supported_exchanges")}
        bottomExtraElement={includeNewlyExchangesCheckbox}
        popupRender={(menu) => (
          <>
            {includeNewlyExchangesCheckbox}
            {menu}
          </>
        )}
      />
      <RequiredFieldMessage
        requirement={supportedExchangesError}
        errorMessage={t("configuration.messages.errors.supported_exchanges")}
        style={ERROR_MESSAGE_STYLE}
      />
    </Item>
  );
};

export default ExchangesSelector;

const ERROR_MESSAGE_STYLE = { marginTop: 4 };

const Item = styled.div`
  width: 100%;
`;
const ItemTitle = styled(Title).attrs({ level: 4 })`
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  font-size: ${(p) => p.theme.typography.fontSizes.S};
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* Field labels in the label role. */
      && {
        margin-bottom: 6px;
        gap: 2px;
        ${newLookType.label};
        color: ${getNewLookTokens(p.theme).secondary};
      }
    `}
`;
const StyledCheckbox = styled(Checkbox)`
  padding: 12px;
  @media (max-width: ${phoneMaxWidth}) {
    padding: 0;
  }
`;
